import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";

import type { ScheduleVisitCardProps } from "features/ScheduleForm/ScheduleForm";

type ExternalServicesState = {
  scheduleProps?: ScheduleVisitCardProps;
  onScheduleTourUrlClick?: () => void;
  /** When set, the header Schedule Tour button opens this URL instead of scrolling to the form. */
  scheduleTourExternalUrl?: string;
};

type ExternalServicesContextValue = {
  state: ExternalServicesState;
  setState: (
    updater: (prev: ExternalServicesState) => ExternalServicesState
  ) => void;
};

const ExternalServicesContext =
  createContext<ExternalServicesContextValue | null>(null);

export const ExternalServicesProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, setState] = useState<ExternalServicesState>({});

  const setStateStable = useCallback(
    (updater: (prev: ExternalServicesState) => ExternalServicesState) => {
      setState((prev) => updater(prev));
    },
    []
  );

  const contextValue = useMemo(
    () => ({ state, setState: setStateStable }),
    [state, setStateStable]
  );

  return (
    <ExternalServicesContext.Provider value={contextValue}>
      {children}
    </ExternalServicesContext.Provider>
  );
};

export const useExternalServicesState = () => {
  const ctx = useContext(ExternalServicesContext);
  if (!ctx) {
    throw new Error(
      "useExternalServicesState must be used within ExternalServicesProvider"
    );
  }
  return ctx.state;
};

export const useOptionalExternalServicesState = () =>
  useContext(ExternalServicesContext)?.state;

export const useSetExternalServicesState = () => {
  const ctx = useContext(ExternalServicesContext);
  if (!ctx) {
    throw new Error(
      "useSetExternalServicesState must be used within ExternalServicesProvider"
    );
  }
  return ctx.setState;
};
